import React, { useState, useRef, useContext, useEffect, CSSProperties } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronDown, ChevronUp, Image as ImageIcon, X, Plus, ZoomIn, User as UserIcon } from 'lucide-react'
import { StyledCard, CardTitle, CollapsePanelHeader } from '../style'
import UserContext from '../../../../../contexts/UserContext'
import jwtDecode from 'jwt-decode'
import axios from '../../../../../config/axios'
import ImageLightbox from '../../../../../components/ImageViewer'
import ConfirmationModal from '../../../../../components/ModalConfirmation'
import InfiniteSelect from '../../../../../components/InfiniteSelect'
import ApiService from '../../../../../services/ApiService'
import AttachmentSourceModal from '../../../../../components/AttachmentSourceModal'
import AttachmentGalleryModal, { GalleryFile } from '../../../../../components/AttachmentGalleryModal'
import AttachmentApiService from '../../../../../services/AttachmentApiService'
import UserAvatar from '../../../../../components/UserAvatar'

interface UserSuggestion {
  id: number
  name: string
  picture?: string
}

const MentionDropdown = styled.ul`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #dfe1e6;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 4px 0;
  z-index: 1200;
  max-height: 180px;
  overflow-y: auto;
`

const MentionItem = styled.li<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #172b4d;
  background: ${({ $active }) => ($active ? '#e8f6fc' : 'transparent')};

  &:hover {
    background: #f4f6f8;
  }
`

const MentionAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`

const MentionHighlight = styled.span`
  color: #0090d3;
  font-weight: 600;
`

const renderCommentWithMentions = (text: string, knownUserNames: string[] = []) => {
  if (!text) return null

  const escapedNames = knownUserNames
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))

  const pattern = escapedNames.length > 0
    ? new RegExp(`(@(?:${escapedNames.join('|')}|[\\wÀ-ÿ]+(?:\\s+[\\wÀ-ÿ]+)?))`, 'g')
    : /(@[\wÀ-ÿ]+(?:\s+[\wÀ-ÿ]+)?)/g

  const parts = text.split(pattern)
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <MentionHighlight key={i}>{part}</MentionHighlight>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  )
}

const NoteThumb = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    cursor: pointer;
  }

  .zoom-overlay {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    opacity: 0;
    transition: opacity 0.2s ease;
    color: #fff;
    pointer-events: none;
    font-size: 8px;
    font-weight: 600;
    text-align: center;
    padding: 2px;
  }

  &:hover .zoom-overlay {
    opacity: 1;
  }
`

const CancelButton = styled.button`
  background-color: #ffffff;
  color: #ef4444;
  border: 1px solid #ef4444;
  padding: 6px 18px;
  border-radius: 100px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #fef2f2;
    border-color: #dc2626;
    color: #dc2626;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SaveButton = styled.button<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? '#e0e0e0' : '#0090d3'};
  color: ${props => props.disabled ? '#999' : 'white'};
  border: none;
  padding: 6px 20px;
  border-radius: 100px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #0077b5;
  }
`

export interface BeneficiamentoNota {
  localId: number
  id?: number
  sectorId: number
  description: string
  imageFiles: File[]
  imageUrls: string[]
  imageOriginalUrls: string[]
  imagePreviews: string[]
  createdByName?: string
  updatedByName?: string
  createdByIdUser?: number | null
  updatedByIdUser?: number | null
  CreatedByUser?: { id?: number; name?: string }
  UpdatedByUser?: { id?: number; name?: string }
  CreatedBy?: { id?: number; name?: string }
  UpdatedBy?: { id?: number; name?: string }
  createdAt?: string | Date
  updatedAt?: string | Date
  mentionedUserIds?: number[]
}

export interface Sector {
  id: number
  name: string
}

interface Props {
  notes: BeneficiamentoNota[]
  onChange: (notes: BeneficiamentoNota[]) => void
  sectors: Sector[]
  isOpen: boolean
  onToggle: () => void
  title?: string
  onSave?: (localId?: number) => Promise<void> | void
  onCancel?: (localId?: number) => void
  isSaving?: boolean
}

const getStoredUserName = (): string => {
  try {
    const token = localStorage.getItem('token')
    if (!token) return ''
    const decoded: any = jwtDecode(token)
    return decoded?.name || decoded?.userName || decoded?.email || ''
  } catch {
    return ''
  }
}

const getStoredUserId = (): number | undefined => {
  try {
    const token = localStorage.getItem('token')
    if (!token) return undefined
    const decoded: any = jwtDecode(token)
    const id = decoded?.sub || decoded?.id || decoded?.userId
    return id ? Number(id) : undefined
  } catch {
    return undefined
  }
}

const formatNoteDate = (dateValue?: string | Date): string => {
  if (!dateValue) return ''
  const d = new Date(dateValue)
  if (isNaN(d.getTime())) return ''
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

let _localIdCounter = 1
export function newBeneficiamentoNota(
  sectorId: number,
  createdByName?: string,
  createdByIdUser?: number | string
): BeneficiamentoNota {
  const name = createdByName || getStoredUserName()
  const id = createdByIdUser !== undefined ? Number(createdByIdUser) : getStoredUserId()
  return {
    localId: _localIdCounter++,
    sectorId,
    description: '',
    imageFiles: [],
    imageUrls: [],
    imageOriginalUrls: [],
    imagePreviews: [],
    createdByName: name,
    CreatedBy: id ? { id, name } : undefined,
    createdAt: new Date().toISOString(),
  }
}

const styles: Record<string, CSSProperties> = {
  noteCard: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  noteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  noteLabel: {
    backgroundColor: '#f3e8ff',
    color: '#7c3aed',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 100,
    whiteSpace: 'nowrap' as const,
    letterSpacing: '0.5px',
  },
  sectorSelect: {
    backgroundColor: '#f3e8ff',
    color: '#7c3aed',
    border: '1px solid #ddd6fe',
    borderRadius: 100,
    fontSize: 13,
    fontWeight: 600,
    padding: '4px 28px 4px 12px',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237c3aed' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '12px',
  },
  authorInfo: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 8,
    backgroundColor: '#e5e7eb',
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 11,
    lineHeight: '16px',
  },
  removeBtn: {
    marginLeft: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    padding: 2,
    borderRadius: 4,
    lineHeight: 1,
  },
  textarea: {
    width: '100%',
    minHeight: 80,
    padding: '10px 14px',
    fontSize: 14,
    color: '#374151',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    resize: 'vertical' as const,
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    lineHeight: '1.5',
  },
  imagesRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
    alignItems: 'center',
  },
  noteFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: 12,
    marginTop: 12,
  },
  noteActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
  },
  removeImageBtn: {
    position: 'absolute' as const,
    top: 2,
    right: 2,
    background: 'rgba(0,0,0,0.55)',
    border: 'none',
    borderRadius: '50%',
    width: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  addImageBox: {
    width: 72,
    height: 72,
    borderRadius: 8,
    border: '1.5px dashed #d1d5db',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: '#fff',
    gap: 2,
  },
  addImageLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#9ca3af',
  },
  addNotaBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    backgroundColor: '#fff',
    border: '1px solid #0090d3',
    borderRadius: 8,
    color: '#0090d3',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
}

const Beneficiamento: React.FC<Props> = ({
  notes,
  onChange,
  sectors,
  isOpen,
  onToggle,
  title = 'Anotações',
  onSave,
  onCancel,
  isSaving = false,
}) => {
  const [searchParams] = useSearchParams()
  const targetNoteId = searchParams.get('noteId') || searchParams.get('anotacaoId') || searchParams.get('beneficiamentoNoteId')
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({})
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false)
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const [activeAttachmentNoteLocalId, setActiveAttachmentNoteLocalId] = useState<number | null>(null)
  const isChoosingAttachmentRef = useRef(false)
  const { user } = useContext(UserContext)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSlides, setLightboxSlides] = useState<{ src: string }[]>([])
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [allSectors, setAllSectors] = useState<Sector[]>(sectors)

  useEffect(() => {
    setAllSectors(sectors)
  }, [sectors])
  const [editingNoteIds, setEditingNoteIds] = useState<Set<number>>(() => {
    const ids = new Set<number>()
    notes.forEach(n => {
      if (!n.id) ids.add(n.localId)
    })
    return ids
  })

  const [activeMentionLocalId, setActiveMentionLocalId] = useState<number | null>(null)
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([])
  const [allUserNames, setAllUserNames] = useState<string[]>([])
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const textareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({})

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`, {
          params: { loadQuantity: 100 },
        })
        const data = response.data?.data || response.data || []
        if (Array.isArray(data)) {
          setAllUserNames(data.map((u: any) => u.name).filter(Boolean))
        }
      } catch {
        // silencioso
      }
    }
    loadUsers()
  }, [])

  const resolveUserPicture = (userLike: any): string | undefined =>
    userLike?.picture || userLike?.avatar || userLike?.avatarUrl || userLike?.profilePicture || userLike?.image || userLike?.photo || undefined

  const fetchUserSuggestions = async (query: string) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`, {
        params: { search: query, loadQuantity: 8 },
      })
      const data = response.data?.data || response.data || []
      setSuggestions(Array.isArray(data) ? data.map((u: any) => ({ id: u.id, name: u.name, picture: resolveUserPicture(u) })) : [])
      setActiveIndex(0)
    } catch {
      setSuggestions([])
    }
  }

  const handleDescriptionChange = (localId: number, value: string, targetEl: HTMLTextAreaElement) => {
    updateNota(localId, { description: value })

    const cursorPos = targetEl.selectionStart ?? value.length
    const textUpToCursor = value.slice(0, cursorPos)
    const atMatch = textUpToCursor.match(/@(\w*)$/)

    if (atMatch) {
      setActiveMentionLocalId(localId)
      setMentionQuery(atMatch[1])
      fetchUserSuggestions(atMatch[1])
    } else {
      if (activeMentionLocalId === localId) {
        setActiveMentionLocalId(null)
        setMentionQuery(null)
        setSuggestions([])
      }
    }
  }

  const selectSuggestion = (localId: number, suggestedUser: UserSuggestion) => {
    const nota = notes.find((n) => n.localId === localId)
    if (!nota) return

    const textarea = textareaRefs.current[localId]
    const cursorPos = textarea?.selectionStart ?? nota.description.length
    const before = nota.description.slice(0, cursorPos)
    const after = nota.description.slice(cursorPos)
    const replaced = before.replace(/@(\w*)$/, `@${suggestedUser.name} `)
    const newDescription = replaced + after

    const currentMentioned = nota.mentionedUserIds || []
    const updatedMentioned = currentMentioned.includes(suggestedUser.id)
      ? currentMentioned
      : [...currentMentioned, suggestedUser.id]

    updateNota(localId, {
      description: newDescription,
      mentionedUserIds: updatedMentioned,
    })

    setActiveMentionLocalId(null)
    setMentionQuery(null)
    setSuggestions([])

    setTimeout(() => {
      if (textarea) {
        textarea.focus()
        const newPos = replaced.length
        textarea.setSelectionRange(newPos, newPos)
      }
    }, 0)
  }

  const handleTextareaKeyDown = (localId: number, e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (activeMentionLocalId === localId && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % suggestions.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        selectSuggestion(localId, suggestions[activeIndex])
        return
      }
      if (e.key === 'Escape') {
        setActiveMentionLocalId(null)
        setMentionQuery(null)
        setSuggestions([])
        return
      }
    }
  }

  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean
    localId: number | null
  }>({
    isOpen: false,
    localId: null,
  })

  const openLightboxForNota = (nota: BeneficiamentoNota, clickedUrl: string) => {
    const allImages = [...nota.imageUrls, ...nota.imagePreviews].map((src) => ({ src }))
    if (!allImages.length) return
    const targetIndex = allImages.findIndex((img) => img.src === clickedUrl)
    setLightboxSlides(allImages)
    setLightboxIndex(Math.max(0, targetIndex))
    setLightboxOpen(true)
  }

  const defaultSectorId = allSectors.length > 0 ? allSectors[0].id : (sectors.length > 0 ? sectors[0].id : 0)

  const canDeleteNota = (nota: BeneficiamentoNota): boolean => {
    if (!nota.id) return true

    const currentUserId = user?.id || user?.sub || getStoredUserId()
    if (nota.CreatedBy?.id && currentUserId) {
      if (String(nota.CreatedBy.id) === String(currentUserId)) {
        return true
      }
    }

    const currentUserName = (user?.name || getStoredUserName() || '').trim().toLowerCase()
    const authorName = (nota.createdByName || nota.CreatedBy?.name || '').trim().toLowerCase()

    if (currentUserName && authorName && currentUserName === authorName) {
      return true
    }

    return false
  }

  const addNota = () => {
    const currentUserName = user?.name || getStoredUserName()
    const currentUserId = user?.id || user?.sub || getStoredUserId()
    const newNota = newBeneficiamentoNota(defaultSectorId, currentUserName, currentUserId)
    onChange([...notes, newNota])
    setEditingNoteIds(prev => new Set(prev).add(newNota.localId))
  }

  const removeNota = (localId: number) => {
    const nota = notes.find(n => n.localId === localId)
    if (!nota || !canDeleteNota(nota)) return
    if (nota.imagePreviews) {
      nota.imagePreviews.forEach(p => URL.revokeObjectURL(p))
    }
    onChange(notes.filter(n => n.localId !== localId))
    setEditingNoteIds(prev => {
      const next = new Set(prev)
      next.delete(localId)
      return next
    })
  }

  const updateNota = (localId: number, patch: Partial<BeneficiamentoNota>) => {
    onChange(notes.map(n => (n.localId === localId ? { ...n, ...patch } : n)))
  }

  const handleFilesChange = (localId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = e.target.files ? Array.from(e.target.files) : []
    if (!files.length) return
    const nota = notes.find(n => n.localId === localId)
    if (!nota) return
    const newPreviews = files.map((f: File) => URL.createObjectURL(f))
    updateNota(localId, {
      imageFiles: [...nota.imageFiles, ...files],
      imagePreviews: [...nota.imagePreviews, ...newPreviews],
    })
    e.target.value = ''
    setActiveAttachmentNoteLocalId(null)
  }

  const handleOpenAttachmentChoice = (localId: number) => {
    if (isSaving) return
    isChoosingAttachmentRef.current = false
    setActiveAttachmentNoteLocalId(localId)
    setIsSourceModalOpen(true)
  }

  const handleChooseUpload = () => {
    isChoosingAttachmentRef.current = true
    if (activeAttachmentNoteLocalId !== null) {
      fileInputRefs.current[activeAttachmentNoteLocalId]?.click()
    }
  }

  const handleChooseGallery = () => {
    isChoosingAttachmentRef.current = true
    setIsGalleryModalOpen(true)
  }

  const handleCloseSourceModal = () => {
    setIsSourceModalOpen(false)
    if (!isChoosingAttachmentRef.current) {
      setActiveAttachmentNoteLocalId(null)
    }
    isChoosingAttachmentRef.current = false
  }

  const handleCloseGalleryModal = () => {
    setIsGalleryModalOpen(false)
    setActiveAttachmentNoteLocalId(null)
  }

  const handleSelectGalleryFiles = async (selectedFiles: GalleryFile[]) => {
    if (activeAttachmentNoteLocalId === null || !selectedFiles?.length) {
      setIsGalleryModalOpen(false)
      setActiveAttachmentNoteLocalId(null)
      return
    }

    const localId = activeAttachmentNoteLocalId
    const nota = notes.find(n => n.localId === localId)
    if (!nota) {
      setIsGalleryModalOpen(false)
      setActiveAttachmentNoteLocalId(null)
      return
    }

    const currentUrls = nota.imageUrls || []
    const currentOriginalUrls = nota.imageOriginalUrls || []

    const newFiles = selectedFiles.filter(f => !currentOriginalUrls.includes(f.fileUrl))
    if (newFiles.length > 0) {
      const newImageUrls = await Promise.all(
        newFiles.map(async f => {
          if (f.signedUrl) return f.signedUrl
          try {
            const signed = await AttachmentApiService.getSignedUrl(f.fileUrl)
            return signed || f.fileUrl
          } catch {
            return f.fileUrl
          }
        })
      )
      const newImageOriginalUrls = newFiles.map(f => f.fileUrl)

      updateNota(localId, {
        imageUrls: [...currentUrls, ...newImageUrls],
        imageOriginalUrls: [...currentOriginalUrls, ...newImageOriginalUrls],
      })
    }

    setIsGalleryModalOpen(false)
    setActiveAttachmentNoteLocalId(null)
  }

  const removeNewImage = (localId: number, idx: number) => {
    const nota = notes.find(n => n.localId === localId)
    if (!nota) return
    URL.revokeObjectURL(nota.imagePreviews[idx])
    const imageFiles = nota.imageFiles.filter((_, i) => i !== idx)
    const imagePreviews = nota.imagePreviews.filter((_, i) => i !== idx)
    updateNota(localId, { imageFiles, imagePreviews })
  }

  const removeExistingImage = (localId: number, idx: number) => {
    const nota = notes.find(n => n.localId === localId)
    if (!nota) return
    updateNota(localId, {
      imageUrls: nota.imageUrls.filter((_, i) => i !== idx),
      imageOriginalUrls: nota.imageOriginalUrls.filter((_, i) => i !== idx),
    })
  }

  return (
    <StyledCard id='product-beneficiamento-section'>
      <CollapsePanelHeader $open={isOpen} onClick={onToggle}>
        <CardTitle>{title}</CardTitle>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </CollapsePanelHeader>

      {isOpen && (
        <>
          {notes.map((nota, index) => {
            const author = nota.createdByName || nota.CreatedByUser?.name || user?.name || getStoredUserName()
            const hasBeenEdited = !!(nota.updatedByIdUser || nota.UpdatedByUser?.id || (nota.updatedByName && nota.updatedByName !== author))
            const updatedAuthor = hasBeenEdited ? (nota.updatedByName || nota.UpdatedByUser?.name || null) : null
            const createdDateStr = formatNoteDate(nota.createdAt)
            const updatedDateStr = formatNoteDate(nota.updatedAt)
            const isEditing = editingNoteIds.has(nota.localId)
            const isTargetNote = !!targetNoteId && (String(nota.id) === targetNoteId || String(nota.localId) === targetNoteId)
            return (
              <div
                key={nota.localId}
                id={`beneficiamento-note-${nota.id || nota.localId}`}
                style={{
                  ...styles.noteCard,
                  ...(isTargetNote
                    ? {
                        borderColor: '#0090d3',
                        boxShadow: '0 0 0 3px rgba(0, 144, 211, 0.25)',
                        transition: 'all 0.3s ease',
                      }
                    : {}),
                }}
              >
                <div style={styles.noteHeader}>
                  <span style={styles.noteLabel}>NOTA {index + 1}</span>
                  <div style={{ position: 'relative', display: 'inline-flex', minWidth: 160 }} onClick={e => e.stopPropagation()}>
                    <InfiniteSelect
                      placeholder='Selecione o setor'
                      mode='crud'
                      allowCreate={isEditing}
                      allowDelete={isEditing}
                      showCreateOnlyIfNotExists
                      minCreateLength={2}
                      options={allSectors}
                      value={allSectors.find(s => s.id === nota.sectorId) || (nota.sectorId ? { id: nota.sectorId, name: (nota as any).sectorName || (nota as any).Sector?.name || `Setor #${nota.sectorId}` } : null)}
                      onSelect={(opt) => {
                        updateNota(nota.localId, {
                          sectorId: opt ? Number(opt.id) : 0,
                          ...(opt?.name ? { sectorName: opt.name } : {}),
                        })
                      }}
                      onItemCreate={async (name: string) => {
                        const res = await ApiService.fashion.createSector({ name })
                        const created = res.data
                        if (created?.id) {
                          setAllSectors(prev => prev.some(s => s.id === created.id) ? prev : [...prev, created])
                          return { id: created.id, name: created.name }
                        }
                        return { id: 0, name }
                      }}
                      onItemDelete={async (id: any) => {
                        await ApiService.fashion.deleteSector(Number(id))
                        setAllSectors(prev => prev.filter(s => s.id !== Number(id)))
                      }}
                      disabled={!isEditing}
                      inputStyle={{
                        backgroundColor: '#f3e8ff',
                        color: '#7c3aed',
                        border: '1px solid #ddd6fe',
                        borderRadius: 100,
                        fontWeight: 600,
                        fontSize: 13,
                        padding: '4px 12px',
                        minWidth: 140,
                        height: 30,
                        cursor: isEditing ? 'pointer' : 'default',
                        opacity: isEditing ? 1 : 0.85,
                      }}
                      dropdownMinWidth={220}
                    />
                  </div>

                  {(author || updatedAuthor) && (
                    <div style={styles.authorInfo}>
                      {author && (
                        <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, fontSize: 11, lineHeight: '16px' }}>
                          <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 500 }}>Criado por:</span>
                          <strong style={{ color: '#374151', fontSize: 11, fontWeight: 600 }}>{author}</strong>
                          {createdDateStr && (
                            <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 400 }}>em {createdDateStr}</span>
                          )}
                        </div>
                      )}
                      {updatedAuthor && (
                        <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, marginLeft: author ? 6 : 0, fontSize: 11, lineHeight: '16px' }}>
                          <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 500 }}>• Editado por:</span>
                          <strong style={{ color: '#374151', fontSize: 11, fontWeight: 600 }}>{updatedAuthor}</strong>
                          {updatedDateStr && (
                            <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 400 }}>em {updatedDateStr}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {canDeleteNota(nota) && (
                    <button
                      type='button'
                      onClick={e => {
                        e.stopPropagation()
                        setDeleteModalState({ isOpen: true, localId: nota.localId })
                      }}
                      style={styles.removeBtn}
                      title='Remover nota'
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                <div style={{ position: 'relative', width: '100%' }}>
                  {activeMentionLocalId === nota.localId && suggestions.length > 0 && mentionQuery !== null && (
                    <MentionDropdown>
                      {suggestions.map((s, idx) => (
                        <MentionItem
                          key={s.id}
                          $active={idx === activeIndex}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            selectSuggestion(nota.localId, s)
                          }}
                        >
                          <UserAvatar src={s.picture} name={s.name} size={24} />
                          <span>{s.name}</span>
                        </MentionItem>
                      ))}
                    </MentionDropdown>
                  )}

                  {isEditing ? (
                    <textarea
                      ref={(el) => { textareaRefs.current[nota.localId] = el }}
                      value={nota.description}
                      onChange={(e) => handleDescriptionChange(nota.localId, e.target.value, e.target)}
                      onKeyDown={(e) => handleTextareaKeyDown(nota.localId, e)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder='Descreva o beneficiamento, instrução ou observação... (@ para mencionar)'
                      style={{
                        ...styles.textarea,
                        backgroundColor: '#fff',
                        borderColor: '#d1d5db',
                        cursor: 'text',
                      }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#0090d3' }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db'
                        setTimeout(() => {
                          if (activeMentionLocalId === nota.localId) {
                            setActiveMentionLocalId(null)
                            setMentionQuery(null)
                            setSuggestions([])
                          }
                        }, 200)
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        ...styles.textarea,
                        backgroundColor: '#f9fafb',
                        borderColor: '#e5e7eb',
                        cursor: 'default',
                        whiteSpace: 'pre-wrap',
                        minHeight: 60,
                      }}
                    >
                      {nota.description ? (
                        renderCommentWithMentions(nota.description, allUserNames)
                      ) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sem descrição</span>
                      )}
                    </div>
                  )}
                </div>

                <div style={styles.noteFooter}>
                  <div style={styles.imagesRow}>
                    {nota.imageUrls.map((url, i) => (
                      <NoteThumb key={`existing-${i}`}>
                        <img
                          src={url}
                          alt={`imagem-${i + 1}`}
                          onClick={e => {
                            e.stopPropagation()
                            openLightboxForNota(nota, url)
                          }}
                          onError={async (e) => {
                            const target = e.target as HTMLImageElement
                            if (!target.dataset.retried && url) {
                              target.dataset.retried = 'true'
                              try {
                                const signed = await AttachmentApiService.getSignedUrl(url)
                                if (signed && signed !== url) {
                                  target.src = signed
                                }
                              } catch {
                                target.dataset.retried = 'failed'
                              }
                            }
                          }}
                        />
                        <div className='zoom-overlay'>
                          <ZoomIn size={16} />
                          <span>Aumentar</span>
                        </div>
                        {isEditing && (
                          <button
                            type='button'
                            style={styles.removeImageBtn}
                            onClick={e => { e.stopPropagation(); removeExistingImage(nota.localId, i) }}
                            title='Remover imagem'
                          >
                            <X size={10} color='#fff' />
                          </button>
                        )}
                      </NoteThumb>
                    ))}

                    {nota.imagePreviews.map((preview, i) => (
                      <NoteThumb key={`new-${i}`}>
                        <img
                          src={preview}
                          alt={`novo-${i + 1}`}
                          onClick={e => {
                            e.stopPropagation()
                            openLightboxForNota(nota, preview)
                          }}
                        />
                        <div className='zoom-overlay'>
                          <ZoomIn size={16} />
                          <span>Aumentar</span>
                        </div>
                        {isEditing && (
                          <button
                            type='button'
                            style={styles.removeImageBtn}
                            onClick={e => { e.stopPropagation(); removeNewImage(nota.localId, i) }}
                            title='Remover imagem'
                          >
                            <X size={10} color='#fff' />
                          </button>
                        )}
                      </NoteThumb>
                    ))}

                    {isEditing && (
                      <>
                        <div
                          style={{
                            ...styles.addImageBox,
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            opacity: isSaving ? 0.6 : 1,
                          }}
                          onClick={e => {
                            e.stopPropagation()
                            handleOpenAttachmentChoice(nota.localId)
                          }}
                          title='Adicionar foto'
                        >
                          <ImageIcon size={22} color='#9ca3af' />
                          <span style={styles.addImageLabel}>FOTO</span>
                        </div>

                        <input
                          ref={el => { fileInputRefs.current[nota.localId] = el }}
                          type='file'
                          accept='image/*'
                          multiple
                          style={{ display: 'none' }}
                          onChange={e => handleFilesChange(nota.localId, e)}
                        />
                      </>
                    )}
                  </div>

                  {(onSave || onCancel) && (
                    <div style={styles.noteActions}>
                      {isEditing ? (
                        <>
                          {onCancel && (
                            <CancelButton
                              type='button'
                              onClick={e => {
                                e.stopPropagation()
                                onCancel(nota.localId)
                                setEditingNoteIds(prev => {
                                  const next = new Set(prev)
                                  next.delete(nota.localId)
                                  return next
                                })
                              }}
                              disabled={isSaving}
                            >
                              Cancelar
                            </CancelButton>
                          )}
                          {onSave && (
                            <SaveButton
                              type='button'
                              onClick={async e => {
                                e.stopPropagation()
                                const currentUserName = user?.name || getStoredUserName()
                                const currentUserId = user?.id || user?.sub || getStoredUserId()
                                updateNota(nota.localId, {
                                  updatedByName: currentUserName,
                                  UpdatedBy: currentUserId ? { id: Number(currentUserId), name: currentUserName } : { name: currentUserName },
                                  updatedAt: new Date().toISOString(),
                                })
                                if (onSave) {
                                  await onSave(nota.localId)
                                }
                                setEditingNoteIds(prev => {
                                  const next = new Set(prev)
                                  next.delete(nota.localId)
                                  return next
                                })
                              }}
                              disabled={isSaving}
                            >
                              {isSaving ? 'Salvando...' : 'Salvar'}
                            </SaveButton>
                          )}
                        </>
                      ) : (
                        <SaveButton
                          type='button'
                          onClick={e => {
                            e.stopPropagation()
                            setEditingNoteIds(prev => new Set(prev).add(nota.localId))
                          }}
                          disabled={isSaving}
                        >
                          Editar
                        </SaveButton>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          <button
            type='button'
            onClick={e => { e.stopPropagation(); addNota() }}
            style={styles.addNotaBtn}
          >
            <Plus size={16} />
            Adicionar Nota
          </button>
        </>
      )}

      <ImageLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
      />

      <ConfirmationModal
        isOpen={deleteModalState.isOpen}
        title="Excluir nota"
        message="Deseja realmente excluir esta nota de anotação? Esta ação não poderá ser desfeita."
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setDeleteModalState({ isOpen: false, localId: null }),
            variant: 'outline',
          },
          {
            label: 'Excluir',
            onClick: () => {
              if (deleteModalState.localId !== null) {
                removeNota(deleteModalState.localId)
              }
              setDeleteModalState({ isOpen: false, localId: null })
            },
            variant: 'danger',
          },
        ]}
      />

      <AttachmentSourceModal
        isOpen={isSourceModalOpen}
        onClose={handleCloseSourceModal}
        title={
          activeAttachmentNoteLocalId !== null
            ? `Adicionar Foto - Nota ${notes.findIndex(n => n.localId === activeAttachmentNoteLocalId) + 1}`
            : 'Adicionar Foto'
        }
        description='Como você deseja adicionar a foto à anotação?'
        uploadLabel='Novo Upload'
        uploadDescription='Enviar do computador ou dispositivo'
        galleryLabel='Escolher da Galeria'
        galleryDescription='Selecionar fotos já salvas na empresa'
        onChooseUpload={handleChooseUpload}
        onChooseGallery={handleChooseGallery}
      />

      <AttachmentGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={handleCloseGalleryModal}
        multiple={true}
        defaultFilter='image'
        title={
          activeAttachmentNoteLocalId !== null
            ? `Galeria da Empresa - Nota ${notes.findIndex(n => n.localId === activeAttachmentNoteLocalId) + 1}`
            : 'Galeria da Empresa - Fotos'
        }
        onSelect={handleSelectGalleryFiles}
      />
    </StyledCard>
  )
}

export { Beneficiamento }
export default Beneficiamento
